/**
 * calendar/weekends.js — the "Show weekends" switch.
 *
 * Weekends are hidden by default: hiding Saturday and Sunday gives each remaining day a
 * wider column, which is what makes the month and week views readable on a phone. The
 * switch (templates/calendar.html #weekendToggle, above the calendar) turns them on, and the
 * choice is remembered per device in localStorage the way the last-chosen view is
 * (CALENDAR_VIEW_KEY). Events dated on a hidden weekend still exist; they are just not drawn.
 *
 * Imports only `state`, so it sits at the edge of the import graph (no cycle).
 */
import { CALENDAR_WEEKENDS_KEY, state } from './state.js';

// Only the grids of days have weekend columns to hide. The switch is shown in these views and
// applies in these views only: every other view gets weekendsViewOptions' override.
export const VIEWS_WITH_WEEKENDS = ['dayGridMonth', 'timeGridWeek', 'timeGridThreeDay'];

/**
 * FullCalendar per-view options that keep every view without the switch on all seven days,
 * so the switch applies exactly where it is visible. A Day view obeying a switch it does not
 * show skips a hidden weekend: Today on a Sunday lands on Monday. Merge into `views`.
 */
export function weekendsViewOptions(viewTypes) {
    return Object.fromEntries(viewTypes
        .filter((type) => !VIEWS_WITH_WEEKENDS.includes(type))
        .map((type) => [type, { weekends: true }]));
}

/** The remembered choice: weekends are shown only when the user turned them on. */
export function readWeekendsPref() {
    try {
        return localStorage.getItem(CALENDAR_WEEKENDS_KEY) === 'true';
    } catch {
        return false;  // localStorage unavailable (private mode, etc.)
    }
}

/** Remember the choice; the Schedule modal's own switch (schedule_modal_controller.js) shares it. */
export function rememberWeekendsPref(shown) {
    try {
        localStorage.setItem(CALENDAR_WEEKENDS_KEY, String(shown));
    } catch {
        // localStorage unavailable — the choice just won't persist
    }
}

/**
 * Put the switch back above the calendar. The calendar is wiped before every build, and a
 * Turbo snapshot can hold the switch inside it (placeWeekendsToggle moved it there), so
 * without this a restored page would lose the switch with the wipe.
 */
export function homeWeekendsToggle(calendarEl) {
    const toggle = document.getElementById('weekendToggle');
    if (toggle && calendarEl.contains(toggle)) calendarEl.before(toggle);
}

// FullCalendar owns the toolbar and re-renders it on view changes, so the switch is
// re-seated beside Today whenever it is not already there.
function placeWeekendsToggle(toggle) {
    const today = document.querySelector('.fc-toolbar .fc-today-button');
    if (today && today.nextElementSibling !== toggle) today.after(toggle);
}

/** Show the switch in the views it applies to, seated beside Today. */
export function updateWeekendsToggle() {
    const toggle = document.getElementById('weekendToggle');
    if (!toggle || !state.calendar) return;
    placeWeekendsToggle(toggle);
    toggle.hidden = !VIEWS_WITH_WEEKENDS.includes(state.calendar.view.type);
}

/**
 * Wire the switch to the calendar. Called on every build, against the freshly rendered
 * page; `onchange` (not addEventListener) so a repeat build replaces the handler.
 */
export function bindWeekendsToggle() {
    const input = document.getElementById('showWeekends');
    if (!input) return;
    input.checked = state.calendar.getOption('weekends');
    input.onchange = () => {
        state.calendar.setOption('weekends', input.checked);
        rememberWeekendsPref(input.checked);
    };
    updateWeekendsToggle();
}
